import { Injectable } from '@angular/core';
import { Network } from '@capacitor/network';
import { Platform } from '@ionic/angular';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { SetLoading } from '../core/loading.state';
import { environment } from '../../environments/environment';
import { AuthorizeCredentials, ContactUsModel, DeviceType, LangPresets, LocalNotification, ResetState } from '../shared';
import { ToastService } from '../core/toast.service';
import { AppFeaturesService } from './app-features.service';
import { finalize, from, lastValueFrom, switchMap, tap } from 'rxjs';
import { FirebaseState } from '../core/firebase.state';
import { AuthState } from './auth.state';
import { Device, DeviceInfo } from '@capacitor/device';
import { App, AppInfo } from '@capacitor/app';
import { PermissionStatus } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { CommunityDevice, CommunityDeviceInfo } from '@capacitor-community/device';

// interface Banner {
// 	text: string | undefined;
// 	show: boolean;
// }

interface AppFeatureModel {
	isApp: boolean;
	appVersion: string;
	usingResponsiveView: boolean;
	notificationsCount: number;
	notifications: LocalNotification[];
	languagePresets: LangPresets[];
	langData: Record<string, string>;
	lang: string;
	filesystemPermission: PermissionStatus | undefined;
	authorizeCredentials: AuthorizeCredentials | undefined;
	deviceInfo: DeviceInfo | undefined;
	deviceDiskInfo: CommunityDeviceInfo | undefined;
	appInfo: AppInfo | undefined;
	deviceId: string | undefined;
}

export class AppFeaturesAuthorizeCredentials {
	static readonly type = '@appFeaturesState.getAuthorizeCredentials';
}

export class AppFeaturesPatchState {
	static readonly type = '@appFeaturesState.patchState';
	constructor(public state: Partial<AppFeatureModel>) {}
}

export class AppFeaturesShowComingSoon {
	static readonly type = '@appFeaturesState.showComingSoon';
}

export class AppFeaturesLangPresets {
	static readonly type = '@appFeaturesState.getLangPresets';
}

export class AppFeaturesLang {
	static readonly type = '@appFeaturesState.getLang';
	constructor(public lang: string) {}
}

export class AppFeaturesNotifications {
	static readonly type = '@appFeaturesState.getNotifications';
	constructor(public getList: boolean) {}
}

export class AppFeaturesContactUs {
	static readonly type = '@appFeaturesState.contactUs';
	constructor(public contactData: ContactUsModel, public reCaptchaToken: string) {}
}

export class UserRegisterDevice {
	static readonly type = '@appFeaturesState.registerDevice';
	constructor(public sendNotifications: boolean) {}
}

@State<AppFeatureModel>({
	name: 'AppFeaturesState',
	defaults: {
		isApp: false,
		appVersion: '0.0.1',
		usingResponsiveView: true,
		notificationsCount: 0,
		notifications: [],
		languagePresets: [],
		langData: {},
		lang: '',
		filesystemPermission: undefined,
		authorizeCredentials: undefined,
		deviceInfo: undefined,
		deviceDiskInfo: undefined,
		appInfo: undefined,
		deviceId: undefined,
	},
})
@Injectable()
export class AppFeaturesState implements NgxsOnInit {
	constructor(
		private platform: Platform,
		private store: Store,
		private toastService: ToastService,
		private appFeaturesService: AppFeaturesService
	) {}

	@Selector()
	public static state(state: AppFeatureModel) {
		return state;
	}

	@Selector()
	public static appVersion(state: AppFeatureModel) {
		return state.appVersion;
	}

	@Selector()
	public static isApp(state: AppFeatureModel) {
		return state.isApp;
	}

	@Selector()
	public static languagePresets(state: AppFeatureModel) {
		return state.languagePresets;
	}

	@Selector()
	public static langData(state: AppFeatureModel) {
		return state.langData;
	}

	@Selector()
	public static lang(state: AppFeatureModel) {
		return state.lang;
	}

	@Selector()
	public static notificationsCount(state: AppFeatureModel) {
		return state.notificationsCount;
	}

	@Selector()
	public static notifications(state: AppFeatureModel) {
		return state.notifications;
	}

	@Selector()
	public static filesystemPermission(state: AppFeatureModel) {
		return state.filesystemPermission;
	}

	@Selector()
	public static authorizeCredentials(state: AppFeatureModel) {
		return state.authorizeCredentials;
	}

	@Selector()
	public static deviceInfo(state: AppFeatureModel) {
		return state.deviceInfo;
	}

	@Selector()
	public static deviceDiskInfo(state: AppFeatureModel) {
		return state.deviceDiskInfo;
	}

	@Selector()
	public static appInfo(state: AppFeatureModel) {
		return state.appInfo;
	}

	@Selector()
	public static deviceId(state: AppFeatureModel) {
		return state.deviceId;
	}

	get filesystemPermission(): PermissionStatus | undefined {
		return this.store.selectSnapshot(AppFeaturesState.filesystemPermission);
	}

	set filesystemPermission(filesystemPermission: PermissionStatus) {
		this.store.dispatch(new AppFeaturesPatchState({ filesystemPermission }));
	}

	get isApp() {
		return this.store.selectSnapshot(AppFeaturesState.isApp);
	}

	get deviceWidth() {
		return this.platform.width();
	}

	get deviceHeight() {
		return this.platform.height();
	}

	get isIos() {
		return this.platform.is('ios');
	}

	get isIPad() {
		return this.platform.is('ipad');
	}

	get isTablet() {
		return this.platform.is('tablet');
	}

	get isNativeIos() {
		return this.platform.is('ios') && Capacitor.isNativePlatform();
	}

	get isAndroid() {
		return this.platform.is('android');
	}
	get isNativeAndroid() {
		return this.platform.is('android') && Capacitor.isNativePlatform();
	}

	get isNative() {
		return this.isNativeAndroid || this.isNativeIos;
	}

	get deviceType(): DeviceType {
		if (this.isNativeAndroid) {
			return DeviceType.ANDROID_APP;
		} else if (this.isNativeIos) {
			return DeviceType.IOS_APP;
		} else {
			return DeviceType.WEB_APP;
		}
	}

	get inDev() {
		return environment.inDev;
	}

	get isMobileBrowser() {
		return this.platform.is('mobileweb');
	}

	get languagePresets() {
		return this.store.selectSnapshot(AppFeaturesState.languagePresets);
	}

	get langData() {
		return this.store.selectSnapshot(AppFeaturesState.langData);
	}

	get lang() {
		return this.store.selectSnapshot(AppFeaturesState.lang);
	}

	get deviceInfo() {
		return this.store.selectSnapshot(AppFeaturesState.deviceInfo);
	}

	get appInfo() {
		return this.store.selectSnapshot(AppFeaturesState.appInfo);
	}

	get appVersion() {
		return this.store.selectSnapshot(AppFeaturesState.appVersion);
	}

	get networkStatus() {
		return Network.getStatus().then((status) => status.connected);
	}

	get authorizeCredentials() {
		return this.store.selectSnapshot(AppFeaturesState.authorizeCredentials);
	}

	async getDeviceDiskInfo() {
		const deviceDiskInfo = await CommunityDevice.getInfo();
		await lastValueFrom(this.store.dispatch(new AppFeaturesPatchState({ deviceDiskInfo })));
	}

	get deviceId() {
		return this.store.selectSnapshot(AppFeaturesState.deviceId);
	}

	async ngxsOnInit() {
		let isApp = false;
		let appInfo = undefined;
		// const isCapacitor = environment.forceApp || Capacitor.isNativePlatform();
		// if (!isCapacitor && this.isMobileBrowser && this.isAndroid) {
		//   this.navigateToPlayStore();
		//   return;
		// }
		// if (!isCapacitor && this.isMobileBrowser && this.isIos) {
		//   this.navigateToAppStore();
		//   return;
		// }

		if (this.platform.is('ios') || this.platform.is('android')) {
			isApp = true;

			if (this.isNative) {
				appInfo = await App.getInfo();
			}
		}

		const appVersion = appInfo?.version || this.store.selectSnapshot(AppFeaturesState.appVersion);
		const deviceInfo = await Device.getInfo();

		const deviceId = (await Device.getId()).identifier;

		const localLangData = await this.appFeaturesService.getLangTags();
		const langData = localLangData?.langData ?? {};
		const lang = localLangData?.lang ?? '';

		// const appStoreBanner: Banner = {
		// 	show: !this.isNative && isApp,
		// 	text: undefined,
		// };
		this.store.dispatch(new AppFeaturesPatchState({ isApp, deviceInfo, deviceId, appInfo, appVersion, lang, langData }));
	}

	@Action(AppFeaturesPatchState)
	private patchNetworkState(ctx: StateContext<AppFeatureModel>, action: AppFeaturesPatchState) {
		ctx.patchState(action.state);
	}

	@Action(AppFeaturesShowComingSoon)
	showComingSoon() {
		this.toastService.showToast(this.isApp, {
			message: "Coming Soon. Feature hasn't been implemented yet",
		});
	}

	@Action(AppFeaturesLangPresets)
	getLangPresets(ctx: StateContext<AppFeatureModel>) {
		return this.appFeaturesService.getLangPresets().pipe(
			tap((data) => {
				ctx.patchState({
					languagePresets: data.data,
				});
			})
		);
	}

	@Action(AppFeaturesLang)
	getLang(ctx: StateContext<AppFeatureModel>, action: AppFeaturesLang) {
		return this.appFeaturesService.getLang(action.lang).pipe(
			tap((data) => {
				this.appFeaturesService.setLangTags(action.lang, data.data);
				ctx.patchState({
					langData: data.data,
					lang: action.lang,
				});
			})
		);
	}

	@Action(AppFeaturesNotifications)
	getNotifications(ctx: StateContext<AppFeatureModel>, action: AppFeaturesNotifications) {
		return this.appFeaturesService.getNotifications(action.getList).pipe(
			tap((data) => {
				const { notifications } = ctx.getState();
				if (data) {
					const notificationsCount = data.unreadNotificationsCount ?? data.data.length;
					const notificationsList = action.getList ? data.data : notifications;

					ctx.patchState({
						notificationsCount: notificationsCount,
						notifications: notificationsList,
					});
				}
			})
		);
	}

	@Action(AppFeaturesContactUs)
	contactUs(ctx: StateContext<AppFeatureModel>, action: AppFeaturesContactUs) {
		ctx.dispatch(new SetLoading(true, this, true, this.contactUs));

		return this.appFeaturesService.contactUs(action.contactData, action.reCaptchaToken).pipe(
			tap((data) =>
				this.toastService.showToast(this.isApp, {
					message: data.message,
					color: 'success',
				})
			),
			finalize(() => ctx.dispatch(new SetLoading(false, this, true, this.contactUs)))
		);
	}

	@Action(UserRegisterDevice)
	registerDevice(ctx: StateContext<AppFeatureModel>, action: UserRegisterDevice) {
		const usersId = this.store.selectSnapshot(AuthState.usersId);
		const deviceToken = this.store.selectSnapshot(FirebaseState.deviceToken);
		if (!usersId) {
			return;
		}
		if (!deviceToken) {
			return;
		}
		ctx.dispatch(new SetLoading(true, this, false, this.registerDevice));
		const deviceId$ = from(Device.getId());
		return deviceId$.pipe(
			switchMap((deviceId) => {
				return this.appFeaturesService.registerDevice(usersId, deviceToken, deviceId.identifier, action.sendNotifications);
			}),
			finalize(() => ctx.dispatch(new SetLoading(false, this, false, this.registerDevice)))
		);
	}

	@Action(AppFeaturesAuthorizeCredentials)
	getAuthorizeCredentials(ctx: StateContext<AppFeatureModel>) {
		return this.appFeaturesService.getAuthorizeCredentials().pipe(
			tap((data) => {
				ctx.patchState({
					authorizeCredentials: data,
				});
			})
		);
	}

	@Action(ResetState)
	resetState(ctx: StateContext<AppFeatureModel>) {
		ctx.setState({
			isApp: this.isApp,
			appVersion: this.appVersion,
			usingResponsiveView: true,
			notificationsCount: 0,
			notifications: [],
			languagePresets: this.languagePresets,
			langData: this.langData,
			lang: this.lang,
			filesystemPermission: undefined,
			authorizeCredentials: this.authorizeCredentials,
			deviceInfo: this.deviceInfo,
			deviceDiskInfo: undefined,
			appInfo: this.appInfo,
			deviceId: this.deviceId,
		});
	}
}
